import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getAdminStats, 
  getSources, 
  toggleSource,
  runDigestNow, 
  getSettings,
  toggleSandboxMode,
  adminLogout,
  sendTestDigest,
  runScheduledDigestNow,
  getDigestJobStatus,
  getSubscribers,
  deleteSubscriber,
  adminConfirmSubscriber,
  addSubscriber,
  getFeedHealth,
  getLookbackWindows,
  getQAStatistics,
  getSerpApiUsage,
  cleanupStaleJobs,
  getRunHistory,
  getCanonicalStoreStats,
  getAnnouncementBanners,
  setAnnouncementBanner,
  clearAnnouncementBanner
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import {
  Play,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  LogOut,
  HelpCircle,
  Users,
  Clock,
  Activity,
  Search,
  Trash2,
  AlertTriangle,
  Circle,
  Send,
  History,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Database,
  Megaphone
} from 'lucide-react';

const BANNER_FREQUENCIES = ['daily', 'weekly', 'biweekly'];
const BANNER_FREQUENCY_LABELS = { daily: 'Daily', weekly: 'Weekly', biweekly: 'Biweekly' };

// Constants
const POLL_INTERVAL_MS = 30000;
const HTTP_UNAUTHORIZED = 401;
const FETCH_DELAY_MS = 5000;

// Section labels mapping
const SECTION_LABELS = {
  'case-law': 'Case Law (UK and EU)',
  'uk-regulators': 'UK Regulators',
  'uk-legislative': 'UK Legislation & Policy',
  'eu': 'EU',
  'eu-regulators': 'EU Regulators',
  'ico': 'ICO',
  'ofcom': 'Ofcom',
  'cybersecurity': 'Cybersecurity',
  'charity-sector': 'Charity & Third Sector',
  'charity-commission': 'Charity Commission',
  'fundraising-regulator': 'Fundraising Regulator',
  'others': 'Others'
};

// Section display order
const SECTION_ORDER = ['case-law', 'uk-regulators', 'uk-legislative', 'eu'];

export default function AdminDashboard({ onLogout }) {
  const [stats, setStats] = useState(null);
  const [sources, setSources] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('admin@digitallawdigest.com');
  const [sendingTestDigest, setSendingTestDigest] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [feedHealth, setFeedHealth] = useState(null);
  const [lookbackWindows, setLookbackWindows] = useState(null);
  const [deletingSubscriber, setDeletingSubscriber] = useState(null);
  const [confirmingSubscriber, setConfirmingSubscriber] = useState(null);
  const [newSubscriberEmail, setNewSubscriberEmail] = useState('');
  const [newSubscriberFrequency, setNewSubscriberFrequency] = useState('daily');
  const [newSubscriberNote, setNewSubscriberNote] = useState('');
  const [addingSubscriber, setAddingSubscriber] = useState(false);
  const [qaStatistics, setQaStatistics] = useState(null);
  const [serpApiUsage, setSerpApiUsage] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [runHistory, setRunHistory] = useState([]);
  const [expandedRun, setExpandedRun] = useState(null);
  const [canonicalStore, setCanonicalStore] = useState(null);

  // Announcement banner state (independent per-frequency text shown at the
  // top of the newsletter). bannerDrafts holds the editable textarea values;
  // savingBanner/clearingBanner track in-flight requests per frequency.
  const [bannerDrafts, setBannerDrafts] = useState({ daily: '', weekly: '', biweekly: '' });
  const [savingBanner, setSavingBanner] = useState({});
  const [clearingBanner, setClearingBanner] = useState({});
  // Last value known to be saved on the server per frequency - used to avoid
  // the 30s poll clobbering text the admin is actively typing but hasn't saved
  const bannerSavedRef = useRef({ daily: '', weekly: '', biweekly: '' });

  // Test Scheduled Send state
  const [scheduledTestEmail, setScheduledTestEmail] = useState('admin@digitallawdigest.com');
  const [scheduledTestFrequency, setScheduledTestFrequency] = useState('daily');
  const [scheduledTestRunning, setScheduledTestRunning] = useState(false);
  const [scheduledTestResults, setScheduledTestResults] = useState(null);
  const [scheduledTestJobId, setScheduledTestJobId] = useState(null);
  const [scheduledTestProgress, setScheduledTestProgress] = useState(null);
  const [scheduledTestDuration, setScheduledTestDuration] = useState(0);
  
  const navigate = useNavigate();

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [statsData, sourcesData, settingsData, subscribersData, feedHealthData, lookbackData, qaStatsData, serpApiData, runHistoryData, canonicalData, bannersData] = await Promise.all([
        getAdminStats(),
        getSources(),
        getSettings(),
        getSubscribers(),
        getFeedHealth(),
        getLookbackWindows(),
        getQAStatistics(),
        getSerpApiUsage(),
        getRunHistory(20),
        getCanonicalStoreStats(),
        getAnnouncementBanners()
      ]);
      setStats(statsData);
      setSources(sourcesData);
      setSettings(settingsData);
      setSubscribers(subscribersData?.subscribers || []);
      setFeedHealth(feedHealthData);
      setLookbackWindows(lookbackData);
      setQaStatistics(qaStatsData);
      setSerpApiUsage(serpApiData);
      setRunHistory(runHistoryData?.runs || []);
      setCanonicalStore(canonicalData);

      // Only sync a frequency's draft from the server if there's no unsaved
      // local edit in progress (draft still matches the last known-saved
      // value) - otherwise the 30s poll would silently overwrite typing.
      const freshBanners = {
        daily: bannersData?.daily || '',
        weekly: bannersData?.weekly || '',
        biweekly: bannersData?.biweekly || ''
      };
      setBannerDrafts(prev => {
        const next = { ...prev };
        for (const freq of BANNER_FREQUENCIES) {
          if (prev[freq] === bannerSavedRef.current[freq]) {
            next[freq] = freshBanners[freq];
          }
        }
        return next;
      });
      bannerSavedRef.current = freshBanners;
    } catch (error) {
      if (error.response?.status === HTTP_UNAUTHORIZED) {
        navigate('/admin/login');
      } else {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRunNow = async () => {
    setRunning(true);
    try {
      await runDigestNow(false);
      toast.success('Digest generation started. Check run history for progress.');
      setTimeout(fetchData, FETCH_DELAY_MS);
    } catch (error) {
      toast.error('Failed to start digest generation');
    } finally {
      setRunning(false);
    }
  };

  const handleToggleSandbox = async (enabled) => {
    try {
      await toggleSandboxMode(enabled);
      setSettings(prev => ({ ...prev, sandbox_mode: enabled }));
      toast.success(`Sandbox mode ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to toggle sandbox mode');
    }
  };

  const handleSaveBanner = async (frequency) => {
    setSavingBanner(prev => ({ ...prev, [frequency]: true }));
    try {
      const message = bannerDrafts[frequency] || '';
      await setAnnouncementBanner(frequency, message);
      bannerSavedRef.current = { ...bannerSavedRef.current, [frequency]: message };
      toast.success(`${BANNER_FREQUENCY_LABELS[frequency]} announcement saved`);
    } catch (error) {
      toast.error(`Failed to save ${BANNER_FREQUENCY_LABELS[frequency]} announcement`);
    } finally {
      setSavingBanner(prev => ({ ...prev, [frequency]: false }));
    }
  };

  const handleClearBanner = async (frequency) => {
    setClearingBanner(prev => ({ ...prev, [frequency]: true }));
    try {
      await clearAnnouncementBanner(frequency);
      setBannerDrafts(prev => ({ ...prev, [frequency]: '' }));
      bannerSavedRef.current = { ...bannerSavedRef.current, [frequency]: '' };
      toast.success(`${BANNER_FREQUENCY_LABELS[frequency]} announcement cleared`);
    } catch (error) {
      toast.error(`Failed to clear ${BANNER_FREQUENCY_LABELS[frequency]} announcement`);
    } finally {
      setClearingBanner(prev => ({ ...prev, [frequency]: false }));
    }
  };

  const handleLogout = async () => {
    try {
      await adminLogout();
      toast.success('Logged out successfully');
      onLogout();
      navigate('/admin/login');
    } catch (error) {
      onLogout();
      navigate('/admin/login');
    }
  };

  const handleSendTestDigest = async (frequency) => {
    if (!testEmailAddress || !testEmailAddress.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSendingTestDigest(frequency);
    try {
      const result = await sendTestDigest(testEmailAddress, frequency, 'scheduled');
      toast.success(result.message || `${frequency} test digest generation started`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || `Failed to send ${frequency} test digest`;
      toast.error(typeof errorMsg === 'string' ? errorMsg : `Failed to send ${frequency} test digest`);
    } finally {
      setSendingTestDigest(null);
    }
  };

  const handleRunScheduledTest = async () => {
    if (!scheduledTestEmail || !scheduledTestEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setScheduledTestRunning(true);
    setScheduledTestResults(null);
    setScheduledTestJobId(null);
    setScheduledTestProgress('Starting...');
    setScheduledTestDuration(0);
    toast.info(`Starting ${scheduledTestFrequency} scheduled send test...`);
    
    try {
      // Start the background job
      const response = await runScheduledDigestNow(scheduledTestEmail, scheduledTestFrequency);
      
      if (response.status === 'started' && response.job_id) {
        setScheduledTestJobId(response.job_id);
        setScheduledTestProgress('Scraping sources...');
        
        // Start polling for status
        const pollInterval = setInterval(async () => {
          try {
            const statusResponse = await getDigestJobStatus(response.job_id);
            setScheduledTestDuration(statusResponse.duration_seconds || 0);
            setScheduledTestProgress(statusResponse.progress || 'Processing...');
            
            if (statusResponse.status === 'completed') {
              clearInterval(pollInterval);
              setScheduledTestResults(statusResponse.results);
              setScheduledTestRunning(false);
              
              if (statusResponse.results?.status === 'failed') {
                toast.error(`Test failed: ${statusResponse.results?.error || 'Unknown error'}`);
              } else if (statusResponse.results?.status === 'email_failed') {
                toast.error('Pipeline completed but email sending failed');
              } else if (statusResponse.results?.status === 'success' && statusResponse.results?.email_sent) {
                if (statusResponse.results?.included_items_count > 0) {
                  toast.success(`Complete — email sent to ${scheduledTestEmail} with ${statusResponse.results.included_items_count} items`);
                } else {
                  toast.info(`Complete — no new content in lookback window. Empty digest email sent to ${scheduledTestEmail}`);
                }
              } else if (statusResponse.results?.status === 'success') {
                toast.warning('Pipeline completed but email may not have sent');
              } else {
                toast.warning(`Test completed with status: ${statusResponse.results?.status}`);
              }
            } else if (statusResponse.status === 'failed') {
              clearInterval(pollInterval);
              setScheduledTestResults({ status: 'failed', error: statusResponse.error });
              setScheduledTestRunning(false);
              toast.error('Test failed: ' + (statusResponse.error?.substring(0, 100) || 'Unknown error'));
            }
            // If 'running' or 'pending', continue polling
          } catch (pollError) {
            console.error('Polling error:', pollError);
            // Don't clear interval on transient errors, just log
          }
        }, 5000); // Poll every 5 seconds
        
        // Safety: stop polling after 15 minutes (900 seconds)
        setTimeout(() => {
          clearInterval(pollInterval);
          if (scheduledTestRunning) {
            setScheduledTestRunning(false);
            setScheduledTestProgress('Timed out waiting for results');
            toast.warning('Job is still running but polling stopped. Check logs.');
          }
        }, 900000);
        
      } else {
        // Unexpected response format
        toast.error(response.error || 'Failed to start job');
        setScheduledTestResults({ status: 'failed', error: response.error || 'Unknown error' });
        setScheduledTestRunning(false);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to start scheduled test';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to start scheduled test');
      setScheduledTestResults({ status: 'failed', error: errorMsg });
      setScheduledTestRunning(false);
    }
  };

  const handleCleanupStaleJobs = async () => {
    if (!window.confirm('This will mark all "running" jobs as "interrupted". Continue?')) {
      return;
    }
    
    try {
      const result = await cleanupStaleJobs();
      toast.success(result.message || 'Stale jobs cleaned up');
      // Refresh stats to update the display
      const newStats = await getAdminStats();
      setStats(newStats);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to cleanup stale jobs';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to cleanup stale jobs');
    }
  };

  const handleDeleteSubscriber = async (subscriber) => {
    if (!window.confirm(`Are you sure you want to permanently remove ${subscriber.email}? This cannot be undone.`)) {
      return;
    }
    
    setDeletingSubscriber(subscriber.token);
    try {
      await deleteSubscriber(subscriber.token);
      setSubscribers(prev => prev.filter(s => s.token !== subscriber.token));
      toast.success(`${subscriber.email} has been permanently removed`);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete subscriber');
    } finally {
      setDeletingSubscriber(null);
    }
  };

  const handleConfirmSubscriber = async (subscriber) => {
    setConfirmingSubscriber(subscriber.token);
    try {
      const result = await adminConfirmSubscriber(subscriber.token);
      if (result.status === 'already_confirmed') {
        toast.info(result.message);
      } else {
        toast.success(result.message);
      }
      // Update local state to reflect confirmation
      setSubscribers(prev => prev.map(s => 
        s.token === subscriber.token 
          ? { ...s, confirmed: true, active: true }
          : s
      ));
      fetchData(); // Refresh stats
    } catch (error) {
      toast.error('Failed to confirm subscriber');
    } finally {
      setConfirmingSubscriber(null);
    }
  };

  const handleAddSubscriber = async (e) => {
    e.preventDefault();
    if (!newSubscriberEmail.trim()) {
      toast.error('Enter an email address');
      return;
    }

    setAddingSubscriber(true);
    try {
      const result = await addSubscriber(newSubscriberEmail.trim(), newSubscriberFrequency, newSubscriberNote.trim() || null);
      toast.success(result.message);
      setNewSubscriberEmail('');
      setNewSubscriberNote('');
      fetchData(); // Refresh subscriber list + stats
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to add subscriber';
      toast.error(errorMsg);
    } finally {
      setAddingSubscriber(false);
    }
  };

  // Format British time
  const formatBritishTime = (date) => {
    return date.toLocaleString('en-GB', {
      timeZone: 'Europe/London',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const getBritishTimezone = () => {
    const month = new Date().toLocaleString('en-US', { timeZone: 'Europe/London', month: 'numeric' });
    return (parseInt(month) >= 4 && parseInt(month) <= 10) ? 'BST' : 'GMT';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleString('en-GB', {
      timeZone: 'Europe/London',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // Get sources with consecutive zero runs (for attention banner)
  const getZeroRunSources = () => {
    return sources.filter(s => s.enabled && (s.consecutive_zeros > 0 || s.items_found === 0));
  };

  // Group sources by section for the source health list
  const getGroupedSources = () => {
    const grouped = {};
    
    // Normalize section names
    sources.forEach(source => {
      let section = source.section || 'others';
      
      // Map sub-sections to main sections
      if (['ico', 'ofcom', 'fca', 'cma'].includes(section)) {
        section = 'uk-regulators';
      } else if (['uk-government', 'dbt', 'dsit', 'desnz', 'nca', 'cybersecurity'].includes(section)) {
        if (source.name?.toLowerCase().includes('enisa')) {
          section = 'eu';
        } else {
          section = 'uk-legislative';
        }
      } else if (section === 'eu-regulators') {
        section = 'eu';
      } else if (['charity-commission', 'fundraising-regulator'].includes(section)) {
        section = 'charity-sector';
      }
      
      if (!grouped[section]) grouped[section] = [];
      grouped[section].push(source);
    });
    
    return grouped;
  };

  // Calculate actual source health counts (only enabled sources)
  const getHealthCounts = () => {
    const enabledSources = sources.filter(s => s.enabled);
    const healthy = enabledSources.filter(s => s.status === 'healthy').length;
    const warning = enabledSources.filter(s => s.status === 'warning').length;
    const issues = enabledSources.filter(s => !['healthy', 'warning'].includes(s.status)).length;
    return { healthy, warning, issues, total: enabledSources.length };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F6] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-[#1A2E4A]" />
      </div>
    );
  }

  const zeroRunSources = getZeroRunSources();
  const groupedSources = getGroupedSources();
  const healthCounts = getHealthCounts();

  return (
    <div className="min-h-screen bg-[#F9F9F6]">
      {/* Top Bar */}
      <header className="bg-white border-b border-[#E2E4E0] px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-lg font-semibold text-[#1A2E4A]">Digital Law Digest</h1>
            {settings?.sandbox_mode && (
              <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-medium rounded">
                SANDBOX
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-6">
            {/* Current BST Time */}
            <div className="flex items-center gap-2 text-sm text-[#525C6A]">
              <Clock className="w-4 h-4" />
              <span className="font-mono">{formatBritishTime(currentTime)} {getBritishTimezone()}</span>
            </div>
            
            {/* Sandbox Toggle */}
            <TooltipProvider>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-[#525C6A]">Sandbox</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1">
                      <Switch
                        checked={settings?.sandbox_mode ?? true}
                        onCheckedChange={handleToggleSandbox}
                        data-testid="sandbox-mode-toggle"
                        className="data-[state=checked]:bg-amber-500"
                      />
                      <HelpCircle className="w-3.5 h-3.5 text-[#525C6A] cursor-help" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs bg-[#1A2E4A] text-white p-3">
                    <p>When enabled, SendGrid accepts requests but does not deliver emails.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>
            
            {/* Run Now Button */}
            <Button
              onClick={handleRunNow}
              disabled={running}
              className="bg-[#C67D3B] hover:bg-[#A86930] text-white px-4 h-9"
              data-testid="run-now-button"
            >
              {running ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              {running ? 'Running...' : 'Run Now'}
            </Button>
            
            {/* View Logs - Better Stack Logtail */}
            <Button
              onClick={() => window.open('https://logs.betterstack.com', '_blank')}
              variant="outline"
              size="sm"
              className="text-[#525C6A] hover:text-[#1A2E4A] border-[#E2E4E0]"
              data-testid="view-logs-button"
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              View Logs
            </Button>
            
            {/* Logout */}
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="text-[#525C6A] hover:text-[#1A2E4A]"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Announcement Banners - independent per-frequency text shown at the
            top of the newsletter, above Today's Highlights, until sent or cleared */}
        <div className="bg-white border border-[#E2E4E0] p-5" data-testid="announcement-banners-panel">
          <div className="flex items-center gap-2 mb-4">
            <Megaphone className="w-4 h-4 text-[#A8672A]" />
            <span className="text-sm font-medium text-[#1A2E4A]">Announcement Banners</span>
            <span className="text-xs text-[#9CA3AF]">Shown above Today's Highlights until sent or cleared</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {BANNER_FREQUENCIES.map(freq => (
              <div key={freq} className="bg-amber-50 border border-amber-200 rounded p-3 flex flex-col gap-2" data-testid={`banner-field-${freq}`}>
                <span className="text-xs font-medium text-amber-800 uppercase tracking-wide">{BANNER_FREQUENCY_LABELS[freq]}</span>
                <Textarea
                  value={bannerDrafts[freq]}
                  onChange={(e) => setBannerDrafts(prev => ({ ...prev, [freq]: e.target.value }))}
                  placeholder={`Announcement for the ${BANNER_FREQUENCY_LABELS[freq].toLowerCase()} edition...`}
                  className="bg-white text-sm min-h-[80px]"
                  data-testid={`banner-textarea-${freq}`}
                />
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleSaveBanner(freq)}
                    disabled={savingBanner[freq]}
                    className="bg-amber-600 hover:bg-amber-700 text-white h-8 px-3"
                    data-testid={`banner-save-${freq}`}
                  >
                    {savingBanner[freq] ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleClearBanner(freq)}
                    disabled={clearingBanner[freq] || !bannerDrafts[freq]}
                    className="text-amber-800 border-amber-300 hover:bg-amber-100 h-8 px-3"
                    data-testid={`banner-clear-${freq}`}
                  >
                    {clearingBanner[freq] ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : 'Clear'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Stats Row - 4 Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Subscribers Card */}
          <div className="bg-white border border-[#E2E4E0] p-5" data-testid="stat-subscribers">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-[#1A2E4A]" />
              <span className="text-sm font-medium text-[#525C6A]">Subscribers</span>
            </div>
            <p className="text-3xl font-bold text-[#1A2E4A] font-mono mb-2">
              {stats?.active_subscribers || 0}
            </p>
            <div className="flex gap-4 text-xs text-[#525C6A]">
              <span>D: <span className="font-mono text-[#1A2E4A]">{stats?.frequency_breakdown?.daily || 0}</span></span>
              <span>B: <span className="font-mono text-[#1A2E4A]">{stats?.frequency_breakdown?.biweekly || 0}</span></span>
              <span>W: <span className="font-mono text-[#1A2E4A]">{stats?.frequency_breakdown?.weekly || 0}</span></span>
            </div>
          </div>

          {/* Last Run Card */}
          <div className="bg-white border border-[#E2E4E0] p-5" data-testid="stat-last-run">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-[#1A2E4A]" />
              <span className="text-sm font-medium text-[#525C6A]">Last Run</span>
            </div>
            <p className="text-lg font-medium text-[#1A2E4A] mb-1">
              {formatDate(stats?.last_run?.started_at)}
            </p>
            {stats?.last_run && (
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${
                  stats.last_run.status === 'success' ? 'bg-green-100 text-green-800' :
                  stats.last_run.status === 'failed' ? 'bg-red-100 text-red-800' :
                  stats.last_run.status === 'running' ? 'bg-blue-100 text-blue-800' :
                  stats.last_run.status === 'interrupted' ? 'bg-amber-100 text-amber-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {stats.last_run.status === 'success' && <CheckCircle className="w-3 h-3" />}
                  {stats.last_run.status === 'failed' && <XCircle className="w-3 h-3" />}
                  {stats.last_run.status === 'running' && <RefreshCw className="w-3 h-3 animate-spin" />}
                  {stats.last_run.status === 'interrupted' && <AlertTriangle className="w-3 h-3" />}
                  {stats.last_run.status}
                </span>
                {stats.last_run.status === 'running' && (
                  <button
                    onClick={handleCleanupStaleJobs}
                    className="text-xs text-red-600 hover:text-red-800 underline"
                    data-testid="clear-stuck-job-btn"
                  >
                    Clear Stuck
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Source Health Card */}
          <div className="bg-white border border-[#E2E4E0] p-5" data-testid="stat-source-health">
            <div className="flex items-center gap-2 mb-3">
              <Activity className="w-4 h-4 text-[#1A2E4A]" />
              <span className="text-sm font-medium text-[#525C6A]">Source Health</span>
            </div>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-3xl font-bold text-[#1A2E4A] font-mono">{healthCounts.healthy}</span>
              <span className="text-sm text-[#525C6A]">/ {healthCounts.total} healthy</span>
            </div>
            <div className="flex gap-3 text-xs">
              <span className="flex items-center gap-1">
                <Circle className="w-2 h-2 fill-green-500 text-green-500" />
                {healthCounts.healthy}
              </span>
              <span className="flex items-center gap-1">
                <Circle className="w-2 h-2 fill-amber-500 text-amber-500" />
                {healthCounts.warning}
              </span>
              <span className="flex items-center gap-1">
                <Circle className="w-2 h-2 fill-red-500 text-red-500" />
                {healthCounts.issues}
              </span>
            </div>
          </div>

          {/* SerpAPI Usage Card */}
          <div className="bg-white border border-[#E2E4E0] p-5" data-testid="stat-serpapi">
            <div className="flex items-center gap-2 mb-3">
              <Search className="w-4 h-4 text-[#1A2E4A]" />
              <span className="text-sm font-medium text-[#525C6A]">SerpAPI Usage</span>
            </div>
            {serpApiUsage?.available ? (
              <>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-3xl font-bold text-[#1A2E4A] font-mono">
                    {serpApiUsage.searches_this_month}
                  </span>
                  <span className="text-sm text-[#525C6A]">/ {serpApiUsage.plan_limit}</span>
                </div>
                <p className="text-xs text-[#525C6A]">
                  Resets {serpApiUsage.reset_date}
                </p>
              </>
            ) : (
              <p className="text-sm text-[#525C6A]">Not configured</p>
            )}
          </div>

          {/* Canonical Store Card */}
          <div className="bg-white border border-[#E2E4E0] p-5" data-testid="stat-canonical-store">
            <div className="flex items-center gap-2 mb-3">
              <Database className="w-4 h-4 text-[#1A2E4A]" />
              <span className="text-sm font-medium text-[#525C6A]">Canonical Store</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="w-3 h-3 text-[#9CA3AF]" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>Persistent item storage ensuring cross-frequency consistency. Items discovered in daily runs remain available for biweekly/weekly editions. Auto-purged after 14 days.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            {canonicalStore ? (
              <>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-3xl font-bold text-[#1A2E4A] font-mono">
                    {canonicalStore.total_items || 0}
                  </span>
                  <span className="text-sm text-[#525C6A]">items stored</span>
                </div>
                {canonicalStore.total_items > 0 ? (
                  <div className="space-y-2">
                    {/* Date range */}
                    {canonicalStore.oldest_item_date && canonicalStore.newest_item_date && (
                      <p className="text-xs text-[#525C6A]">
                        {new Date(canonicalStore.oldest_item_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        {' → '}
                        {new Date(canonicalStore.newest_item_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      </p>
                    )}
                    {/* Section breakdown */}
                    {canonicalStore.by_section && Object.keys(canonicalStore.by_section).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 text-xs">
                        {Object.entries(canonicalStore.by_section).map(([section, count]) => (
                          <span key={section} className="px-1.5 py-0.5 bg-[#F5F5F3] text-[#525C6A] rounded text-[10px]">
                            {SECTION_LABELS[section] || section}: {count}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Recent activity */}
                    <p className="text-[10px] text-[#9CA3AF]">
                      Last 24h: {canonicalStore.last_24h || 0} • Last 72h: {canonicalStore.last_72h || 0} • TTL: {canonicalStore.ttl_days}d
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-[#9CA3AF]">No items yet — run a digest to populate</p>
                )}
              </>
            ) : (
              <p className="text-sm text-[#525C6A]">Loading...</p>
            )}
          </div>
        </div>

        {/* Attention Banner - Sources with zero items */}
        {zeroRunSources.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 p-4" data-testid="attention-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800 mb-2">
                  {zeroRunSources.length} source{zeroRunSources.length !== 1 ? 's' : ''} with consecutive zero-item runs
                </p>
                <div className="flex flex-wrap gap-2">
                  {zeroRunSources.map(source => (
                    <span 
                      key={source.name}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded"
                    >
                      {source.name}
                      {source.consecutive_zeros > 1 && (
                        <span className="font-mono bg-amber-200 px-1 rounded">
                          x{source.consecutive_zeros}
                        </span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column: Lookback Windows + Subscribers */}
          <div className="space-y-6">
            {/* Lookback Windows */}
            <div className="bg-white border border-[#E2E4E0] p-5" data-testid="lookback-windows">
              <h3 className="font-semibold text-[#1A2E4A] mb-4">Lookback Windows</h3>
              <p className="text-xs text-[#525C6A] mb-3">All cutoffs are midnight-based (start of day)</p>
              <div className="space-y-2 text-sm">
                {lookbackWindows?.frequencies?.map(freq => (
                  <div key={freq.name} className="flex justify-between py-2 border-b border-[#E2E4E0] last:border-0">
                    <span className="text-[#525C6A] capitalize font-medium">{freq.name}</span>
                    <span className="font-mono text-[#1A2E4A] text-xs">
                      {freq.lookback_start_formatted || 'N/A'}
                    </span>
                  </div>
                )) || (
                  <p className="text-xs text-[#525C6A]">Loading...</p>
                )}
              </div>
            </div>

            {/* Subscribers List */}
            <div className="bg-white border border-[#E2E4E0] p-5" data-testid="subscribers-list">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-[#1A2E4A]">Subscribers</h3>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#525C6A]">
                    {subscribers.filter(s => s.confirmed && s.active).length} active
                  </span>
                  {subscribers.filter(s => !s.confirmed).length > 0 && (
                    <span className="text-xs text-amber-600">
                      ({subscribers.filter(s => !s.confirmed).length} pending)
                    </span>
                  )}
                </div>
              </div>
              <form onSubmit={handleAddSubscriber} className="mb-4 p-3 bg-[#F9F9F6] rounded space-y-2" data-testid="add-subscriber-form">
                <p className="text-xs font-medium text-[#1A2E4A]">Add subscriber directly (pre-confirmed, no email sent)</p>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="subscriber@example.com"
                    value={newSubscriberEmail}
                    onChange={(e) => setNewSubscriberEmail(e.target.value)}
                    className="flex-1 h-9"
                    data-testid="add-subscriber-email"
                  />
                  <select
                    value={newSubscriberFrequency}
                    onChange={(e) => setNewSubscriberFrequency(e.target.value)}
                    className="h-9 px-2 border border-[#E2E4E0] rounded text-sm text-[#1A2E4A] bg-white"
                    data-testid="add-subscriber-frequency"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Biweekly</option>
                  </select>
                </div>
                <Input
                  type="text"
                  placeholder="Optional note - e.g. migrated from Emergent, originally subscribed 2026-03-15"
                  value={newSubscriberNote}
                  onChange={(e) => setNewSubscriberNote(e.target.value)}
                  className="h-9 text-xs"
                  data-testid="add-subscriber-note"
                />
                <Button
                  type="submit"
                  disabled={addingSubscriber || !newSubscriberEmail.trim()}
                  size="sm"
                  className="w-full"
                  data-testid="add-subscriber-submit"
                >
                  {addingSubscriber ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    'Add Subscriber'
                  )}
                </Button>
              </form>
              <div className="max-h-80 overflow-y-auto space-y-2">
                {subscribers.length === 0 ? (
                  <p className="text-sm text-[#525C6A] text-center py-4">No subscribers yet</p>
                ) : (
                  <>
                    {/* Unconfirmed subscribers first */}
                    {subscribers.filter(s => !s.confirmed).length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-amber-600 mb-2 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          Pending Confirmation
                        </p>
                        {subscribers.filter(s => !s.confirmed).map(sub => (
                          <div 
                            key={sub.token}
                            className="flex items-center justify-between py-2 px-3 bg-amber-50 border border-amber-200 rounded mb-2"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-[#1A2E4A] truncate">{sub.email}</p>
                              <p className="text-xs text-amber-600 capitalize">{sub.frequency} • awaiting confirmation</p>
                            </div>
                            <div className="flex items-center gap-1">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleConfirmSubscriber(sub)}
                                      disabled={confirmingSubscriber === sub.token}
                                      className="text-green-600 hover:text-green-700 hover:bg-green-50 h-8 w-8 p-0"
                                      data-testid={`confirm-subscriber-${sub.token}`}
                                    >
                                      {confirmingSubscriber === sub.token ? (
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <CheckCircle className="w-4 h-4" />
                                      )}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Manually confirm (bypass email verification)</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteSubscriber(sub)}
                                disabled={deletingSubscriber === sub.token}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                              >
                                {deletingSubscriber === sub.token ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Trash2 className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Confirmed subscribers */}
                    {subscribers.filter(s => s.confirmed && s.active).length > 0 && (
                      <div>
                        {subscribers.filter(s => !s.confirmed).length > 0 && (
                          <p className="text-xs font-medium text-green-600 mb-2 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Active Subscribers
                          </p>
                        )}
                        {subscribers.filter(s => s.confirmed && s.active).map(sub => (
                          <div 
                            key={sub.token}
                            className="flex items-center justify-between py-2 px-3 bg-[#F9F9F6] rounded mb-2"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-[#1A2E4A] truncate">{sub.email}</p>
                              <p className="text-xs text-[#525C6A] capitalize">{sub.frequency}</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteSubscriber(sub)}
                              disabled={deletingSubscriber === sub.token}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                            >
                              {deletingSubscriber === sub.token ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Right Column: Test Send + QA Statistics */}
          <div className="space-y-6">
            {/* Test Send Form */}
            <div className="bg-white border border-[#E2E4E0] p-5" data-testid="test-send-form">
              <h3 className="font-semibold text-[#1A2E4A] mb-2">Send Test Digest</h3>
              <p className="text-xs text-amber-600 mb-4 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Test emails only send to the admin address below, not to subscribers
              </p>
              <div className="flex gap-2 mb-4">
                <Input
                  type="email"
                  placeholder="admin@digitallawdigest.com"
                  value={testEmailAddress}
                  onChange={(e) => setTestEmailAddress(e.target.value)}
                  className="flex-1 h-9"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                {['daily', 'biweekly', 'weekly'].map(freq => (
                  <Button
                    key={freq}
                    onClick={() => handleSendTestDigest(freq)}
                    disabled={sendingTestDigest === freq || !testEmailAddress}
                    variant="outline"
                    size="sm"
                    className="capitalize"
                  >
                    {sendingTestDigest === freq ? (
                      <RefreshCw className="w-3 h-3 animate-spin mr-1" />
                    ) : (
                      <Send className="w-3 h-3 mr-1" />
                    )}
                    {freq}
                  </Button>
                ))}
              </div>
            </div>

            {/* Test Scheduled Send - Uses production code path */}
            <div className="bg-white border border-[#E2E4E0] p-5" data-testid="test-scheduled-send">
              <h3 className="font-semibold text-[#1A2E4A] mb-2">Test Scheduled Send</h3>
              <p className="text-xs text-blue-600 mb-4 flex items-center gap-1">
                <Activity className="w-3 h-3" />
                Runs the exact production scheduled send code path (~2 min)
              </p>
              
              <div className="space-y-3 mb-4">
                <div className="flex gap-2">
                  <select
                    value={scheduledTestFrequency}
                    onChange={(e) => setScheduledTestFrequency(e.target.value)}
                    className="h-9 px-3 border border-[#E2E4E0] rounded-md text-sm bg-white"
                    disabled={scheduledTestRunning}
                  >
                    <option value="daily">Daily</option>
                    <option value="biweekly">Biweekly</option>
                    <option value="weekly">Weekly</option>
                  </select>
                  <Input
                    type="email"
                    placeholder="admin@digitallawdigest.com"
                    value={scheduledTestEmail}
                    onChange={(e) => setScheduledTestEmail(e.target.value)}
                    className="flex-1 h-9"
                    disabled={scheduledTestRunning}
                  />
                </div>
                
                <Button
                  onClick={handleRunScheduledTest}
                  disabled={scheduledTestRunning || !scheduledTestEmail}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  size="sm"
                >
                  {scheduledTestRunning ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      Running...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4 mr-2" />
                      Run Scheduled Test
                    </>
                  )}
                </Button>
                
                {/* Progress Display while running */}
                {scheduledTestRunning && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-blue-700">
                        {scheduledTestProgress || 'Processing...'}
                      </span>
                      <span className="text-xs text-blue-600 font-mono">
                        {Math.floor(scheduledTestDuration / 60)}:{String(Math.floor(scheduledTestDuration % 60)).padStart(2, '0')}
                      </span>
                    </div>
                    {scheduledTestJobId && (
                      <p className="text-xs text-blue-500">Job ID: {scheduledTestJobId}</p>
                    )}
                    <p className="text-xs text-blue-500 mt-1">Polling every 5s...</p>
                  </div>
                )}
              </div>
              
              {/* Results Display */}
              {scheduledTestResults && (
                <div className={`mt-4 p-3 rounded-md text-sm ${
                  scheduledTestResults.status === 'failed' || scheduledTestResults.status === 'email_failed'
                    ? 'bg-red-50 border border-red-200'
                    : scheduledTestResults.status === 'success' && scheduledTestResults.email_sent && scheduledTestResults.included_items_count > 0
                    ? 'bg-green-50 border border-green-200'
                    : scheduledTestResults.status === 'success' && scheduledTestResults.email_sent && (scheduledTestResults.included_items_count === 0 || !scheduledTestResults.included_items_count)
                    ? 'bg-blue-50 border border-blue-200'
                    : 'bg-amber-50 border border-amber-200'
                }`}>
                  <div className="flex items-center gap-2 mb-2 font-medium">
                    {scheduledTestResults.status === 'failed' ? (
                      <><XCircle className="w-4 h-4 text-red-600" /> Test Failed</>
                    ) : scheduledTestResults.status === 'email_failed' ? (
                      <><XCircle className="w-4 h-4 text-red-600" /> Email Sending Failed</>
                    ) : scheduledTestResults.status === 'success' && scheduledTestResults.email_sent && scheduledTestResults.included_items_count > 0 ? (
                      <><CheckCircle className="w-4 h-4 text-green-600" /> Email Sent Successfully</>
                    ) : scheduledTestResults.status === 'success' && scheduledTestResults.email_sent && (scheduledTestResults.included_items_count === 0 || !scheduledTestResults.included_items_count) ? (
                      <><AlertCircle className="w-4 h-4 text-blue-600" /> Complete — No New Content in Window</>
                    ) : scheduledTestResults.status === 'success' ? (
                      <><AlertCircle className="w-4 h-4 text-amber-600" /> Completed — Email May Not Have Sent</>
                    ) : (
                      <><AlertCircle className="w-4 h-4 text-amber-600" /> Completed with Issues</>
                    )}
                  </div>
                  
                  {scheduledTestResults.error && (
                    <p className="text-red-700 text-xs mb-2">{scheduledTestResults.error}</p>
                  )}
                  
                  {scheduledTestResults.duration_seconds && (
                    <p className="text-xs text-[#525C6A] mb-2">
                      Duration: {Math.round(scheduledTestResults.duration_seconds)}s | 
                      HTML: {scheduledTestResults.html_length?.toLocaleString()} chars |
                      Items: {scheduledTestResults.included_items_count || 0}
                    </p>
                  )}
                  
                  {scheduledTestResults.sections && Object.keys(scheduledTestResults.sections).length > 0 && (
                    <div className="mt-2 space-y-1">
                      <p className="text-xs font-medium text-[#1A2E4A]">Section Results:</p>
                      {Object.entries(scheduledTestResults.sections).map(([section, data]) => (
                        <div key={section} className="flex justify-between text-xs">
                          <span className="text-[#525C6A]">{SECTION_LABELS[section] || section}</span>
                          <span className={data.is_empty ? 'text-amber-600' : 'text-green-600'}>
                            {data.relevant_items || 0} items {data.is_empty ? '(empty)' : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* QA Statistics - All 3 frequencies side by side */}
            <div className="bg-white border border-[#E2E4E0] p-5" data-testid="qa-statistics">
              <h3 className="font-semibold text-[#1A2E4A] mb-4">QA Statistics</h3>
              <div className="grid grid-cols-3 gap-4">
                {['daily', 'biweekly', 'weekly'].map(freq => {
                  const freqStats = qaStatistics?.by_frequency?.[freq] || {};
                  return (
                    <div key={freq} className="text-center">
                      <p className="text-xs text-[#525C6A] capitalize mb-2">{freq}</p>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between px-2">
                          <span className="text-[#525C6A]">Items</span>
                          <span className="font-mono text-[#1A2E4A]">{freqStats.total_items || 0}</span>
                        </div>
                        <div className="flex justify-between px-2">
                          <span className="text-[#525C6A]">Relevant</span>
                          <span className="font-mono text-green-600">{freqStats.relevant || 0}</span>
                        </div>
                        <div className="flex justify-between px-2">
                          <span className="text-[#525C6A]">Excluded</span>
                          <span className="font-mono text-red-600">{freqStats.excluded || 0}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Run History Panel */}
        <div className="bg-white border border-[#E2E4E0] p-5" data-testid="run-history-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-[#1A2E4A] flex items-center gap-2">
              <History className="w-4 h-4" />
              Run History
            </h3>
            <span className="text-xs text-[#525C6A]">Last 14 days</span>
          </div>
          
          {runHistory.length === 0 ? (
            <p className="text-sm text-[#525C6A] text-center py-8">No runs recorded yet</p>
          ) : (
            <div className="space-y-2">
              {runHistory.slice(0, 10).map((run) => (
                <div key={run.id} className="border border-[#E2E4E0] rounded-lg overflow-hidden">
                  {/* Run Header - Clickable */}
                  <div 
                    className="flex items-center justify-between p-3 hover:bg-[#F9F9F6] cursor-pointer"
                    onClick={() => setExpandedRun(expandedRun === run.id ? null : run.id)}
                  >
                    <div className="flex items-center gap-3">
                      {run.status === 'success' ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : run.status === 'failed' ? (
                        <XCircle className="w-4 h-4 text-red-500" />
                      ) : (
                        <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />
                      )}
                      <div>
                        <div className="text-sm font-medium text-[#1A2E4A]">
                          {run.type === 'scheduled' ? 'Scheduled Run' : run.type === 'manual' ? 'Manual Run' : 'Test Run'}
                        </div>
                        <div className="text-xs text-[#525C6A]">
                          {run.started_at ? new Date(run.started_at).toLocaleString('en-GB', {
                            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                          }) : 'Unknown time'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-sm font-mono text-[#1A2E4A]">
                          {run.subscribers_sent || 0} sent
                        </div>
                        <div className="text-xs text-[#525C6A]">
                          {(run.frequencies_active || []).join(', ') || 'No frequencies'}
                        </div>
                      </div>
                      {expandedRun === run.id ? (
                        <ChevronUp className="w-4 h-4 text-[#525C6A]" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-[#525C6A]" />
                      )}
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {expandedRun === run.id && (
                    <div className="border-t border-[#E2E4E0] p-3 bg-[#F9F9F6]">
                      {/* Frequency Stats */}
                      {run.frequency_stats && Object.keys(run.frequency_stats).length > 0 && (
                        <div className="mb-3">
                          <div className="text-xs font-medium text-[#525C6A] mb-2">Items Per Frequency</div>
                          <div className="grid grid-cols-3 gap-2">
                            {['daily', 'biweekly', 'weekly'].map(freq => {
                              const stats = run.frequency_stats[freq];
                              if (!stats) return null;
                              return (
                                <div key={freq} className="bg-white p-2 rounded border border-[#E2E4E0]">
                                  <div className="text-xs font-medium text-[#1A2E4A] uppercase">{freq}</div>
                                  <div className="text-lg font-mono font-bold text-[#1A2E4A]">{stats.items_included || 0}</div>
                                  <div className="text-xs text-[#525C6A]">
                                    {stats.items_in_window || 0} in window
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Error Message */}
                      {run.error_message && (
                        <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700">
                          <strong>Error:</strong> {run.error_message}
                        </div>
                      )}
                      
                      {/* Run ID */}
                      <div className="text-xs text-[#525C6A] mt-2 font-mono">
                        ID: {run.id}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Source Health - Full List Grouped by Section */}
        <div className="bg-white border border-[#E2E4E0] p-5" data-testid="source-health-full">
          <h3 className="font-semibold text-[#1A2E4A] mb-4">Source Health</h3>
          
          <div className="space-y-6">
            {SECTION_ORDER.map(sectionKey => {
              const sectionSources = groupedSources[sectionKey] || [];
              if (sectionSources.length === 0) return null;
              
              return (
                <div key={sectionKey}>
                  <h4 className="text-sm font-medium text-[#525C6A] mb-3 pb-2 border-b border-[#E2E4E0]">
                    {SECTION_LABELS[sectionKey] || sectionKey}
                  </h4>
                  <div className="space-y-2">
                    {sectionSources.map(source => (
                      <div 
                        key={source.name}
                        className="flex items-center justify-between py-2 px-3 hover:bg-[#F9F9F6] rounded transition-colors"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {/* Status Dot */}
                          <Circle className={`w-2.5 h-2.5 flex-shrink-0 ${
                            !source.enabled ? 'fill-gray-300 text-gray-300' :
                            source.status === 'healthy' ? 'fill-green-500 text-green-500' :
                            source.status === 'warning' ? 'fill-amber-500 text-amber-500' :
                            'fill-red-500 text-red-500'
                          }`} />
                          
                          {/* Name */}
                          <span className={`text-sm truncate ${!source.enabled ? 'text-gray-400' : 'text-[#1A2E4A]'}`}>
                            {source.name}
                          </span>
                          
                          {/* Zero Badge */}
                          {source.enabled && source.consecutive_zeros > 0 && (
                            <span className="flex-shrink-0 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs font-mono rounded">
                              0x{source.consecutive_zeros}
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-4 text-xs text-[#525C6A]">
                          {/* Item Count */}
                          <span className="font-mono w-12 text-right">
                            {source.items_found ?? 0} items
                          </span>
                          
                          {/* Last Scraped */}
                          <span className="w-28 text-right">
                            {source.last_checked ? formatDate(source.last_checked) : 'Never'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            
            {/* Others section if any remain */}
            {Object.keys(groupedSources).filter(k => !SECTION_ORDER.includes(k)).map(sectionKey => {
              const sectionSources = groupedSources[sectionKey];
              if (!sectionSources || sectionSources.length === 0) return null;
              
              return (
                <div key={sectionKey}>
                  <h4 className="text-sm font-medium text-[#525C6A] mb-3 pb-2 border-b border-[#E2E4E0]">
                    {SECTION_LABELS[sectionKey] || sectionKey}
                  </h4>
                  <div className="space-y-2">
                    {sectionSources.map(source => (
                      <div 
                        key={source.name}
                        className="flex items-center justify-between py-2 px-3 hover:bg-[#F9F9F6] rounded transition-colors"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <Circle className={`w-2.5 h-2.5 flex-shrink-0 ${
                            !source.enabled ? 'fill-gray-300 text-gray-300' :
                            source.status === 'healthy' ? 'fill-green-500 text-green-500' :
                            source.status === 'warning' ? 'fill-amber-500 text-amber-500' :
                            'fill-red-500 text-red-500'
                          }`} />
                          <span className={`text-sm truncate ${!source.enabled ? 'text-gray-400' : 'text-[#1A2E4A]'}`}>
                            {source.name}
                          </span>
                          {source.enabled && source.consecutive_zeros > 0 && (
                            <span className="flex-shrink-0 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs font-mono rounded">
                              0x{source.consecutive_zeros}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-[#525C6A]">
                          <span className="font-mono w-12 text-right">
                            {source.items_found ?? 0} items
                          </span>
                          <span className="w-28 text-right">
                            {source.last_checked ? formatDate(source.last_checked) : 'Never'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}
